<template>
    <v-container fluid light>
        <v-row align="center" justify="center">
            <v-col class="d-flex" cols="12" sm="6">
                <v-text-field v-model="search"
                              append-icon="mdi-magnify"
                              label="Search"
                              @click:append="searchMaterial"
                              @keydown.enter="searchMaterial"
                              single-line
                              :hint="hint"
                              persistent-hint></v-text-field>
            </v-col>
        </v-row>
        <v-row align="center" justify="center">
            <v-col>
                <v-data-table :headers="filteredHeaders"
                              fixed-header
                              fixed-footer
                              height="600px"
                              :items="materials"
                              :options.sync="options"
                              :server-items-length="totalMaterials"
                              :loading="materialsLoading"
                              :footer-props="footerProps"
                              class="elevation-1"
                              dense>

                    <template v-slot:[`header.quantity`]="{ header }">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on">{{ header.text }}</span>
                                <v-icon x-small
                                        color="secondary"
                                        v-bind="attrs"
                                        v-on="on">mdi-help-circle-outline</v-icon>
                            </template>
                            <span>Quantity of the specified material for the current department ({{ locationDepartmentId }}) on the active order.</span>
                        </v-tooltip>
                    </template>
                    <template v-slot:[`header.isFavorite`]="{ header }">
                        <v-tooltip top>
                            <template v-slot:activator="{ on, attrs }">
                                <span v-bind="attrs" v-on="on">{{ header.text }}</span>
                                <v-icon color="gray"
                                        v-bind="attrs"
                                        style="opacity: 0.4"
                                        v-on="on">mdi-star</v-icon>
                            </template>
                            <span>Add/Remove item from Department ({{locationDepartmentId}}) Favorites</span>
                        </v-tooltip>
                    </template>
                    <template v-slot:top>
                        <v-layout row px-6>
                            <v-flex px-2 pt-2 xs12 md2>
                                <v-toolbar-title>Materials</v-toolbar-title>
                            </v-flex>
                            <v-flex px-2 pt-2 xs12 md3>

                                <v-select :items="favoritesList"
                                          item-text="listName"
                                          item-value="id"
                                          label="Department Favorites"
                                          :loading="favoritesListsLoading"
                                          v-model="selectedFavoritesListId"
                                          append-outer-icon="mdi-refresh"
                                          @change="search=''"
                                          @click:append-outer="clearFavorites()">
                                </v-select>

                            </v-flex>

                            <v-flex px-2 pt-2 xs12 md3>
                                <v-select :items="materialGroups"
                                          item-text="name"
                                          item-value="id"
                                          label="SAP Material Groups"
                                          :loading="materialGroupsLoading"
                                          v-model="selectedGroup"
                                          append-outer-icon="mdi-refresh"
                                          @click:append-outer="clearMaterialGroups()"></v-select>
                            </v-flex>

                            <v-flex px-2 pt-2 xs12 md3>
                                <AddMaterialForm v-if="hasAccess('ADHOCMATERIAL') && (enableAdhoc) || hasAccess('BYPASSADHOC')" @clearSearch="clearSearch" />
                            </v-flex>


                        </v-layout>

                    </template>

                    <template v-slot:[`item.description`]="{ item }">
                        <span><MaterialAliasForm :item="item" /></span>
                        <span v-if="item.alias && item.alias.length > 0" class="font-weight-bold">({{ item.alias }})&nbsp;</span>
                        {{ item.description }}
                        <v-chip class="ma-2" color="red" small outlined v-if="!item.isActive">
                            Inactive
                            <v-icon right small>mdi-lock-alert</v-icon>
                        </v-chip>
                    </template>

                    <template v-slot:[`item.isFavorite`]="{ item }">
                        <v-icon @click="setFavorite(item, !item.isDefaultFavorite)" :style="getItemStyle(item)">mdi-star</v-icon>
                        &nbsp;
                        <FavoritesButton :materialItem="item" :favoritesLists="item.favoritesLists" :defaultFavoriteListId="defaultFavoriteListId"></FavoritesButton>
                    </template>

                    <template v-slot:[`item.quantity`]="{ item }">
                        <QuantityInput v-model.number="item.quantity"
                                       :rules="[rules.positiveInteger]"
                                       :disabled="!item.isActive||item.isBusy||!canEdit"
                                       :errorMessages="item.errorMessage"
                                       :update="quantityUpdate"
                                       :el="generateElementName"
                                       v-bind:ref="generateElementName(item, 'quantityElement')"
                                       :item="item"
                                       :onMoveNext="nextItem"
                                       :onMovePrevious="previousItem">
                        </QuantityInput>
                    </template>
                    <template v-slot:[`item.currentPrice`]="{ item }">
                        <UnitPriceInput
                            v-model.number="item.currentPrice"
                            :rules="[rules.positiveInteger]"
                            :disabled="!item.isActive||item.isBusy||!canEdit"
                            :update="onPriceChange"
                            :el="generateElementName"
                            v-bind:ref="generateElementName(item, 'priceElement')"
                            :onMoveNext="nextItem"
                            :onMovePrevious="previousItem"
                            :item="item"
                            :errorMessages="item.priceError"
                            v-if="item.id > 0"
                        >
                        </UnitPriceInput>
                    </template>
                    <template v-slot:[`item.totalPrice`]="{ item }">
                        <div class="text-right justify-center">{{ formatCurrency(item.quantity * item.currentPrice) }}</div>
                    </template>
                    <template v-slot:[`header.totalPrice`]="{ header }">
                        <div class="text-right justify-center">{{ header.text }}</div>
                    </template>
                    <template v-slot:[`header.currentPrice`]="{ header }">
                        <div class="text-right justify-center">{{ header.text }}</div>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
    </v-container>
</template>
<script>
    import AddMaterialForm from './AddMaterialForm.vue';
    import MaterialAliasForm from './MaterialAliasForm.vue';
    import QuantityInput from '@/components/QuantityInput.vue';
    import UnitPriceInput from '@/components/UnitPriceInput.vue';
    import { mapActions, mapGetters } from 'vuex';
    import appsettings from '../../appsettings.js';
    import FavoritesButton from '@/components/FavoritesButton.vue';
    import axios from "axios";


    export default {
        data() {
            return {
                locationId: null,
                requisitionOrderId: null,
                locationDepartmentId: null,
                canReloadData: false,
                enableAdhoc: false,
                totalMaterials: 0,
                materials: [],
                materialGroups: [],
                favoritesList: [],
                favoritesListClone: [],
                materialsLoading: true,
                materialGroupsLoading: true,
                favoritesListsLoading: true,
                requisitionOrder: {},
                search: "",
                showActiveMaterialsOnly: true,
                selectedGroup: 0,
                selectedFavoritesListId: -1,
                selectedFavoritesList: {},
                hint: "Searching ALL MATERIALS",
                unwatchOptions: null,
                unwatchSelectedGroup: null,
                unwatchShowActive: null,
                unwatchselectedFavoritesListId: null,
                showingFavorites: false,
                defaultFavoriteListId: 0,
                options: {},
                rules: {
                    positiveInteger: value => !isNaN(parseInt(value)) >= 0 || 'Positive integer required.',
                },
                footerProps: {
                    'items-per-page-options': appsettings.config.pages
                },
                headers: [
                    {
                        text: 'Material ID',
                        align: 'start',
                        value: 'xId',
                        width: '7%',
                        sortable: false
                    },
                    {
                        text: '',
                        value: 'isFavorite',
                        width: '7%',
                        sortable: false
                    },
                    {
                        text: 'Quantity',
                        value: 'quantity',
                        width: '8%',
                        sortable: false
                    },
                    {
                        text: 'UoM',
                        value: 'uomId',
                        width: '4%',
                        sortable: false
                    },
                    {
                        text: 'Name',
                        value: 'description',
                        width: '67%',
                        sortable: false
                    },
                    {
                        text: 'Unit Price',
                        value: 'currentPrice',
                        width: '8%',
                        sortable: false
                    },
                    {
                        text: 'Total Price',
                        value: 'totalPrice',
                        sortable: false
                    },
                ],
            }
        },
        components: {
            AddMaterialForm,
            QuantityInput,
            UnitPriceInput,
            MaterialAliasForm,
            FavoritesButton           
        },
        computed: {
            ...mapGetters(['getVrsUser', 'hasAccess', 'getActiveOrder']),
            filteredHeaders() {
                if (!this.getVrsUser.showPricing) {
                    return this.headers.filter(header => header.value != 'currentPrice' && header.value != 'totalPrice');
                }
                return this.headers;
            },
            canEdit() {
                return (
                    (this.requisitionOrder.orderStateDetails.isOpen && (this.hasAccess('MATERIALRO'))) || 
                    (this.requisitionOrder.orderStateDetails.isProcessing && (this.hasAccess('PROCESSRO')))
                ) 
            },
        },
        watch: {

        },
        created() {
            if (this.$route.params.requisitionOrderId == null || this.$route.params.requisitionOrderId == -1 || this.$route.params.requisitionOrderId == 0) {
                this.$router.push({
                    name: "orderhistory",
                    params: { locationId: this.$route.params.locationId || this.getVrsUser.locationId }
                });
            }
        },
        async mounted() {

            this.locationId = this.$route.params.locationId;
            this.locationDepartmentId = this.$route.params.locationDepartmentId;
            this.requisitionOrderId = this.$route.params.requisitionOrderId;
            this.requisitionOrder = await this.getRequisitionOrder();
            this.options.itemsPerPage = this.getVrsUser.pageSize ?? appsettings.default.pageSize;
            this.showActiveMaterialsOnly = this.getVrsUser.showActiveMaterials ?? appsettings.default.showActiveMaterials;

            await this.getMaterialGroupsFromApi();
            await this.getFavoritesListsFromApi();

            await this.getDataFromApi();

            //Set the watches programmatically after the data has loaded to avoid race-conditions.
            //Assign the watches to an "unwatch" so we can disable them when route values change.
            this.unwatchselectedFavoritesListId = this.$watch('selectedFavoritesListId', this.watchSelectedFavoriteListId);
            this.unwatchSelectedGroup = this.$watch('selectedGroup', this.watchSelectedGroup);
            this.unwatchShowActive = this.$watch('showActiveMaterialsOnly', this.watchShowActive);
            this.unwatchOptions = this.$watch('options', this.watchOptions, { deep: true });

        },
        methods: {
            ...mapActions(["sendMessage","switchActiveOrder"]),
            getItemStyle(item) {
                
                var styleObject = {
                    color: 'gray',
                    opacity: 0.2,
                    visibility: 'visible'
                };

                if (item.isDefaultFavorite) {
                    styleObject.color = 'orange';
                    styleObject.opacity = 1;
                } else if (item.isFavorite){
                    styleObject.color = 'orange';
                    styleObject.opacity = 0.3;
                }

                if (item.isAdhoc) {
                    styleObject.visibility = 'hidden';
                }

                return styleObject;
            },
            async getMaterialsForRoute(route) {
              this.materialsLoading = true;
              let response = await axios.get(route);
              var pageMetadata = JSON.parse(response.headers["x-pagination"]);
              let data = response.data;
              var x = 0;

              data.forEach((item) => {

                item.isBusy = false;
                if (item.isActive) {
                  item.sequence = x++;
                } else {
                  item.sequence = 0;
                }
                item.favoritesLists = this.favoritesListClone;

                item.materialFavoriteLists.forEach((f) => {
                  if (f.favorite.materialFavoriteListId == this.defaultFavoriteListId) {
                    item.isDefaultFavorite = true;
                  }
                })
              })

              this.materialsLoading = false;
              this.materials = data;
              this.totalMaterials = pageMetadata.TotalCount;
              return data;
            },
            async getDataFromApi() {
              const { sortBy, sortDesc, page, itemsPerPage } = this.options;
              const terms = this.search;
              return await this.getMaterialsForRoute(`/api/material/requisitionorder/${this.requisitionOrderId}/locationdepartment/${this.locationDepartmentId}?group=${this.selectedGroup}&favoriteslist=${this.selectedFavoritesListId}&onlyactive=${this.showActiveMaterialsOnly}&PageNumber=${page}&PageSize=${itemsPerPage}&terms=${terms}`);

            },
            async getDataForFavorites() {
              const { sortBy, sortDesc, page, itemsPerPage } = this.options;
              const terms = this.search;
              let orderId = this.getActiveOrder;
              return await this.getMaterialsForRoute(`/api/material/requisitionorder/${this.requisitionOrderId}/locationdepartment/${this.locationDepartmentId}/favorites?locationDepartmentId=${this.locationDepartmentId}&PageNumber=${page}&PageSize=${itemsPerPage}`);

            },
            async getMaterialGroupsFromApi() {

                let response = await axios.get(`/api/Lookup/materialgroups`);
                let data = response.data;
                data.sort((a, b) => a.name.localeCompare(b.name));
                data.unshift({
                    id: 0,
                    isActive: true,
                    name: "ALL MATERIALS",
                    originId: "",
                    updatedBy: "",
                    updatedDate: "",
                    xId: 0
                });

                this.materialGroups = data;
                this.materialGroupsLoading = false;

                return data;
            },
            async getFavoritesListsFromApi() {

              let response = await axios.get(`/api/MaterialFavoriteList/departmentlist/${this.locationDepartmentId}`);
              let data = response.data;

              data.sort((a, b) => a.listName.localeCompare(b.listName));

              this.favoritesList = data;
              this.favoritesListClone = this.favoritesList.slice(); 

              this.favoritesList.unshift({
                  id: 0,
                  listName: "ALL FAVORITES",
                  locationDepartmentId: null,
                  isDefault: false,
                  originId: "",
                  updatedBy: "",
                  updatedDate: "",
                  xId: 0
              });

              this.favoritesList.unshift({
                  id: -1,
                  listName: "ALL MATERIALS",
                  locationDepartmentId: null,
                  isDefault: false,
                  originId: "",
                  updatedBy: "",
                  updatedDate: "",
                  xId: -1
              });

              this.favoritesList.forEach((item) => {
                  if (item.isDefault) {
                      this.defaultFavoriteListId = item.id;
                  } 
              });

              this.favoritesListsLoading = false;
              return data;

            },
            async addMaterialToRo(item) {

                item.locationDepartmentId = this.locationDepartmentId;
                item.locationId = this.locationId;
                try {
                    let response = await axios.post(`/api/RequisitionOrder/${this.requisitionOrderId}/material`,item);

                    //Must use Object.assign to transfer updated values
                    //to the underlying reactive object and maintain reactive props.
                    item = Object.assign(item, response.data);
                } catch(e) {
                    this.sendMessage({
                        color: "error",
                        text: `Failed to add ${item.xId} to the active requisition order.`,
                        timeout: 6000
                    })
                }
                finally {
                    this.materialsLoading = false;
                }

                return item;

            },
            async updateMaterial(item) {
                let response = await axios.put(`/api/material`, item);
                item = Object.assign(item, response.data);
                this.materialsLoading = false;

                return item;
            },
            formatCurrency(price) {
                let formatter = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD'
                });

                return formatter.format(price);
            },
            async clearSearch() {
                this.search = "";
                this.enableAdhoc = false;
                await this.searchMaterial();
            },
            async searchMaterial() {
                this.canReloadData = false;
                this.selectedGroup = 0;
                this.options.page = 1;
                if (this.search.length > 3) {
                    this.enableAdhoc = true;
                } else {
                    this.enableAdhoc = false;
                }

                await this.getDataFromApi();

                this.canReloadData = true;

            },
            generateElementName(item) {
                return 'quantityElement' + item.sequence;
            },
            async quantityUpdate(item, previousValue) {
                item.locationDepartmentId = this.locationDepartmentId;
                item.locationId = this.locationId;
                item.errorMessage = null;

                if (!item.quantity) {
                    item.quantity = 0;
                }

                if (item.quantity < 0 || item.quantity == previousValue) {
                    return;
                }

                item.isBusy = true;

                await this.addMaterialToRo(item);

                item.isBusy = false;

                if (item.hasError) {
                    item.errorMessage = "An error occurred. Item was not updated.";
                }
               
            },

            async onPriceChange(item, previousValue) {

                if (!item.currentPrice) {
                    item.currentPrice = 0;
                }

                if (item.currentPrice == previousValue) {
                    return;
                }

                if (item.currentPrice < 0) {
                    item.priceError = 'Price must be equals or greater than $0.00.';
                    item.currentPrice = previousValue;
                    return;
                }
                
                item.currentPrice = parseFloat(parseFloat(item.currentPrice.toString().replace(/[^\d.]/g, "")).toFixed(2));

                if (item.quantity > 0) {
                    await this.addMaterialToRo(item);
                } else {
                    await this.updateMaterial(item);
                }
            },

            async clearFavorites() {
                this.selectedFavoritesListId = -1;
                this.showingFavorites = false;
            },

            async setFavorite(item, isDefault) {
            
                item.locationDepartmentId = this.locationDepartmentId;
                item.defaultMaterialFavoriteId = this.defaultFavoriteListId;
                item.isDefaultFavorite = isDefault;

                let response = await axios.post(`/api/MaterialFavorite/materialfavorite`,item)
                let data = response.data;
                if (!isDefault) {

                    for (var i = 0; i < item.materialFavoriteLists.length; i++) {

                        if (item.materialFavoriteLists[i].favorite.id == data.favorite.id) {
                            item.materialFavoriteLists.splice(i, 1);
                            break;
                        }
                    }
                    
                } else {
                    item.materialFavoriteLists.push(data);
                }

                item.isFavorite = item.materialFavoriteLists.length > 0;

                this.materialsLoading = false;
                if (isDefault) {
                    this.sendMessage({
                        color: "success",
                        text: "Item has been added to your default favorites list.",
                        timeout: 2000
                    });
                } else {
                    this.sendMessage({
                        color: "success",
                        text: "Item has been removed from your default favorites list.",
                        timeout: 2000
                    });
                }

            },

            async watchOptions(newValue, oldValue) {
                await this.getDataFromApi();
            },
            async watchSelectedGroup(newValue, oldValue) {
                await this.getDataFromApi();
            },
            async clearMaterialGroups() {
                this.selectedGroup = 0;
            },
            findFavorite(item) {
                return item.id == this.selectedFavoritesListId;
            },
            async watchSelectedFavoriteListId(newValue, oldValue) {
                this.selectedFavoritesList = this.favoritesList.find(this.findFavorite);
                if (this.selectedFavoritesListId == 0) {
                    //Show All Favorites
                    await this.getDataForFavorites();
                } else {
                    await this.getDataFromApi();
                }
                
            },
            async watchShowActive(newValue, oldValue) {
                await this.getDataFromApi();
            },

            nextItem(item, prefix) {
                const index = prefix + (item.sequence + 1);
                if (this.$refs[index]) {
                    this.$refs[index].focus(index);
                } else {
                    this.$refs[prefix + (item.sequence)].blur(prefix + (item.sequence));
                }
            },
            previousItem(item, prefix) {

                const index = prefix + (item.sequence - 1);
                if (this.$refs[index]) {
                    this.$refs[index].focus(index);
                } else {
                    this.$refs[prefix + (item.sequence)].blur(prefix + (item.sequence));
                }
            },
            async getRequisitionOrder() {
                let response = await axios.get(`/api/RequisitionOrder/${this.$route.params.requisitionOrderId}`);
                return response.data;
            },
        }
    }
</script>